// src/screens/MyVideosScreen.js
// ============================================================================
// MY VIDEOS — owner manage view (Videos system, approved 2026-07-07)
//
// TWO CLASSES OF VIDEO, TWO DIFFERENT RULES (founder 2026-08-03) — the library
// is unreadable unless the screen says which is which:
//  - STREAM VIDEOS (we recorded them) are kept for the tier's retention window.
//    Days are the only number this screen quotes for them.
//  - UPLOADS (the member's own files) are NEVER time-expired by that window, so
//    they must never render a countdown. On a free account, or after a paid
//    membership ends, the inactivity ladder may remove them after a set period,
//    with email + in-app notice first (ToS 1.4 §12).
// The GB ceiling underneath both is an unadvertised abuse backstop (it still
// FIFO-evicts oldest-first), so it is never rendered. Videos land PRIVATE;
// publishing to the public profile is opt-in (founder Q3).
// Download = signed URLs for the owner's own segment files (founder markup).
//
// LAPSE GRACE (step 2.3b, ToS 1.3): when a paid membership ends, recordings
// made as a member are not removed at the free window. The server warns the
// member (in-app, push and email) and keeps them until lapseGraceUntil. While
// that date is set this screen shows a banner with the date and a Renew
// button, and those recordings read "removed on {date}" instead of a
// countdown. Without the fields the screen behaves exactly as before.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, ActivityIndicator,
  StyleSheet, Switch, Platform, Modal, Pressable, Linking, Image,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import { membershipErrorMessage } from '../utils/membershipPayment';
import {
  isTierKey, WHO_CAN_WATCH, WHO_CAN_WATCH_OPTIONS, VIDEO_TIER_UNREACHABLE, whoCanWatchBlocked,
} from '../utils/membershipTiers';

const IS_WEB = Platform.OS === 'web';

const fmtGB = (bytes) => `${(Math.max(0, bytes || 0) / 1e9).toFixed(1)} GB`;
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};
const fmtDate = (msOrIso) => new Date(msOrIso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
// The lapse grace date, formatted exactly as the server's warning email does
// (vods.js formatRemovalDate). The grace ends at 23:59:59.999 America/Chicago,
// so formatting it in the viewer's own zone would show the next day east of
// Chicago's offset (e.g. "October 9" in the UK for an email that says October 8).
const fmtRemovalDate = (ms) => {
  try {
    return new Date(ms).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago',
    });
  } catch {
    return new Date(ms).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
};

export default function MyVideosScreen({ navigation }) {
  const { showError } = useError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null); // { id, title, description, tags, game, thumbnailUrl }
  const [uploading, setUploading] = useState(null); // { name, pct, finalizing }
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // bulk-select video ids

  const load = useCallback(async () => {
    try {
      const res = await httpsCallable(getFunctions(), 'getMyVideos')({});
      setData(res.data);
    } catch (err) {
      console.error('[MyVideos] load failed:', err);
      showError('Could not load your videos', err?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (video) => {
    setBusy(`pub:${video.id}`);
    try {
      await httpsCallable(getFunctions(), 'updateVideo')({ videoId: video.id, published: !video.published });
      setData((d) => ({
        ...d,
        videos: d.videos.map((v) => (v.id === video.id ? { ...v, published: !video.published } : v)),
      }));
    } catch (err) {
      showError('Could not update video', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // Channel memberships (3.5a): Private / Members / Public, on rows where
  // members-only applies (see usesVisibility below; every other row keeps the
  // Switch above, unchanged). A refusal shows the server's sentence, and the
  // list is fetched again either way, so the control redraws from the
  // server's truth (a held choice included).
  const setVisibility = async (video, visibility) => {
    setBusy(`vis:${video.id}`);
    try {
      await httpsCallable(getFunctions(), 'updateVideo')({ videoId: video.id, visibility });
    } catch (err) {
      showError('Could not update video', membershipErrorMessage(err));
    } finally {
      await load();
      setBusy(null);
    }
  };

  // Tiers (R5, 5.12 C8): who can watch a members-only video, from a tier up
  // (missing = Spark). Immediate, like unpublishing. The server applies a
  // change on an existing members-only video without a reach check, so this
  // screen offers only the minimums getMyVideos says a tier reaches
  // (membersVideosTiers) plus the current one; any other shows C10 and is
  // never sent. The list reloads after every change.
  const setMinTier = async (video, membersMinTier) => {
    setBusy(`tier:${video.id}`);
    try {
      await httpsCallable(getFunctions(), 'updateVideo')({ videoId: video.id, membersMinTier });
    } catch (err) {
      showError('Could not update video', membershipErrorMessage(err));
    } finally {
      await load();
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy('edit');
    try {
      await httpsCallable(getFunctions(), 'updateVideo')({
        videoId: editing.id,
        title: editing.title,
        description: editing.description,
        tags: editing.tags.split(',').map((t) => t.trim()).filter(Boolean),
        game: editing.game || null,
      });
      setEditing(null);
      await load();
    } catch (err) {
      showError('Could not save changes', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // Upload a custom thumbnail for the video being edited. Same pick→base64→
  // callable flow as the stream-channel thumbnail. Reflects on the list card
  // immediately (no full reload needed).
  const pickVideoThumbnail = async () => {
    if (!editing) return;
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      showError('Permission required', 'Allow photo access to upload a thumbnail.');
      return;
    }
    const picked = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true, aspect: [16, 9], quality: 0.6, base64: true,
    });
    if (picked.canceled) return;
    try {
      setUploadingThumb(true);
      const asset = picked.assets[0];
      let imageBase64 = asset.base64;
      if (!imageBase64) {
        const blob = await (await fetch(asset.uri)).blob();
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      const { data } = await httpsCallable(getFunctions(), 'uploadVideoThumbnail')({
        videoId: editing.id, imageBase64, contentType: 'image/jpeg',
      });
      const url = data?.url || null;
      if (url) {
        setEditing((e) => e && ({ ...e, thumbnailUrl: url }));
        setData((d) => d && ({ ...d, videos: d.videos.map((v) => (v.id === editing.id ? { ...v, thumbnailUrl: url } : v)) }));
      }
    } catch (err) {
      showError('Thumbnail upload failed', err.message || 'Keep it under 2MB (1280×720 recommended).');
    } finally {
      setUploadingThumb(false);
    }
  };

  // Read a local file's duration via a detached <video> element (web only).
  // Cosmetic — the card's duration label; playback uses the real file anyway.
  const readDuration = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(vid.duration) || 0); };
      vid.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      vid.src = url;
    } catch { resolve(0); }
  });

  const UPLOAD_ACCEPT = { 'video/mp4': true, 'video/webm': true, 'video/quicktime': true };

  const uploadVideo = () => {
    if (!data?.serving) {
      showError('Uploads unavailable', "Video uploads aren't available on your account right now.");
      return;
    }
    if (!IS_WEB) {
      showError('Not available here', 'Video upload is available on the web app.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const contentType = UPLOAD_ACCEPT[file.type] ? file.type
        : (/\.webm$/i.test(file.name) ? 'video/webm' : /\.mov$/i.test(file.name) ? 'video/quicktime' : 'video/mp4');
      try {
        setUploading({ name: file.name, pct: 0 });
        const durationSeconds = await readDuration(file);

        const startFn = httpsCallable(getFunctions(), 'startVideoUpload');
        const { data: start } = await startFn({ sizeBytes: file.size, contentType });

        // Resumable chunked upload: 16MB chunks with Content-Range, each chunk
        // retried on its own — a network blip costs one chunk, not the whole
        // multi-GB file. GCS answers 308 for "send more", 200/201 when done.
        const CHUNK = 16 * 1024 * 1024; // must be a multiple of 256KB
        const putChunk = (offset, end) => new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', start.uploadUrl);
          xhr.setRequestHeader('Content-Range', `bytes ${offset}-${end - 1}/${file.size}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const done = offset + e.loaded;
              setUploading((u) => (u ? { ...u, pct: Math.min(99, Math.round((done / file.size) * 100)) } : u));
            }
          };
          xhr.onload = () => ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 308
            ? resolve()
            : reject(new Error(`Upload failed (${xhr.status}). Try again.`)));
          xhr.onerror = () => reject(new Error('network'));
          xhr.send(file.slice(offset, end));
        });

        // Resumable status probe: asks GCS how many bytes it has persisted so
        // a retry continues from there instead of resending or losing data.
        const queryOffset = () => new Promise((resolve) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', start.uploadUrl);
          xhr.setRequestHeader('Content-Range', `bytes */${file.size}`);
          xhr.onload = () => {
            if (xhr.status === 308) {
              const range = xhr.getResponseHeader('Range'); // "bytes=0-N" once data persisted
              resolve(range ? parseInt(range.split('-')[1], 10) + 1 : 0);
            } else if (xhr.status >= 200 && xhr.status < 300) {
              resolve(file.size); // already complete
            } else {
              resolve(-1); // unknown — retry the current chunk as-is
            }
          };
          xhr.onerror = () => resolve(-1);
          xhr.send();
        });

        let offset = 0;
        let attempt = 0;
        while (offset < file.size) {
          const end = Math.min(offset + CHUNK, file.size);
          try {
            await putChunk(offset, end);
            offset = end;
            attempt = 0;
          } catch (err) {
            attempt += 1;
            if (err.message !== 'network' || attempt >= 4) {
              throw err.message === 'network'
                ? new Error('Upload failed — check your connection and try again.')
                : err;
            }
            await new Promise((r) => setTimeout(r, attempt * 2000)); // 2s/4s/6s backoff
            const persisted = await queryOffset();
            if (persisted >= 0) offset = Math.min(persisted, file.size);
          }
        }

        setUploading((u) => (u ? { ...u, pct: 100, finalizing: true } : u));
        const finFn = httpsCallable(getFunctions(), 'finalizeVideoUpload');
        const { data: fin } = await finFn({
          uploadId: start.uploadId,
          title: file.name.replace(/\.[^.]+$/, '').slice(0, 80),
          durationSeconds,
        });
        setUploading(null);
        await load();
        if (fin?.evicted?.length) {
          // Same wording as the server's VIDEO_READY push. evictedFreeEraOnly
          // (finalizeVideoUpload) = the owner has no paid membership, so only
          // videos held after it ended were taken. Missing on an older server.
          showError('Video uploaded', `To make room, your oldest ${fin.evicted.length === 1 ? '' : `${fin.evicted.length} `}${fin.evictedFreeEraOnly ? 'free-plan ' : ''}${fin.evicted.length === 1 ? 'video was' : 'videos were'} deleted.`);
        }
      } catch (err) {
        setUploading(null);
        // Server refusals carry a "token: " prefix (e.g. "storage-full: Your
        // storage is full...") — show only the sentence.
        showError('Could not upload video', String(err?.message || '').replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
      }
    };
    input.click();
  };

  const download = async (video) => {
    setBusy(`dl:${video.id}`);
    try {
      const res = await httpsCallable(getFunctions(), 'getVideoDownloadUrls')({ videoId: video.id });
      const parts = res.data?.parts || [];
      if (parts.length === 0) {
        showError('Nothing to download', 'No video files found.');
        return;
      }
      if (IS_WEB) {
        // Long sessions are multiple 5-min part files; trigger each download.
        parts.forEach((p, i) => {
          setTimeout(() => {
            const a = document.createElement('a');
            a.href = p.url;
            a.download = p.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }, i * 600);
        });
        if (parts.length > 1) {
          showError('Downloading', `${parts.length} video parts are downloading. A single-file download is coming with clipping.`);
        }
      } else {
        Linking.openURL(parts[0].url);
      }
    } catch (err) {
      showError('Could not prepare download', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (video) => {
    const confirmed = IS_WEB
      ? window.confirm(`Delete "${video.title}" permanently?\n\nThis can't be undone. Download it first if you want a copy.`)
      : true;
    if (!confirmed) return;
    setBusy(`del:${video.id}`);
    try {
      await httpsCallable(getFunctions(), 'deleteVideo')({ videoId: video.id });
      await load();
    } catch (err) {
      showError('Could not delete video', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // ---- Bulk selection: download or delete many at once (founder 2026-07-25) --
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set((data?.videos || []).map((v) => v.id)));
  const allSelected = (data?.videos || []).length > 0 && selected.size === (data?.videos || []).length;

  const downloadSelected = async () => {
    if (!IS_WEB) return;
    const vids = (data?.videos || []).filter((v) => selected.has(v.id));
    setBusy('bulk-dl');
    try {
      for (const v of vids) {
        const res = await httpsCallable(getFunctions(), 'getVideoDownloadUrls')({ videoId: v.id });
        (res.data?.parts || []).forEach((p, j) => setTimeout(() => {
          const a = document.createElement('a');
          a.href = p.url; a.download = p.name;
          document.body.appendChild(a); a.click(); a.remove();
        }, j * 400));
        await new Promise((r) => setTimeout(r, 700)); // stagger so the browser doesn't drop downloads
      }
    } catch (err) {
      showError('Download failed', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const deleteSelected = async () => {
    const vids = (data?.videos || []).filter((v) => selected.has(v.id));
    if (vids.length === 0) return;
    const confirmed = IS_WEB
      ? window.confirm(`Delete ${vids.length} video${vids.length === 1 ? '' : 's'} permanently?\n\nThis can't be undone. Download anything you want to keep first.`)
      : true;
    if (!confirmed) return;
    setBusy('bulk-del');
    try {
      for (const v of vids) {
        await httpsCallable(getFunctions(), 'deleteVideo')({ videoId: v.id });
      }
      clearSelection();
      await load();
    } catch (err) {
      showError('Could not delete videos', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}><ActivityIndicator size="large" color="#FCD000" /></View>
        <NavBar navigation={navigation} />
      </View>
    );
  }

  const videos = data?.videos || [];
  const storage = data?.storage || { usedBytes: 0, capBytes: 0, tier: 'free', warnAtPct: 85 };
  const retentionDays = Number(storage.retentionDays || 0);
  // Near the silent ceiling: still worth warning about (FIFO eviction is real),
  // but the numbers behind it stay off-screen.
  const usedPct = storage.capBytes > 0 ? Math.min(100, Math.round((storage.usedBytes / storage.capBytes) * 100)) : 0;
  const nearFull = storage.capBytes > 0 && usedPct >= storage.warnAtPct;
  const tierName = storage.tier.charAt(0).toUpperCase() + storage.tier.slice(1);

  // Lapse grace (step 2.3b). getMyVideos sets lapseGraceUntil (ms) only while
  // a lapsed member's grace is running AND something is due on it;
  // lapseGraceVideoCount is how many member-era recordings go on that date
  // (server countLapseGraceVideos, the same number the email used). Missing/
  // null for everyone else = no banner, no new labels, today's behavior.
  const lapseGraceRaw = data?.lapseGraceUntil ?? null;
  const lapseGraceUntilMs = (typeof lapseGraceRaw === 'string' ? Date.parse(lapseGraceRaw) : Number(lapseGraceRaw)) || 0;
  const lapseGraceCount = Math.max(0, Math.floor(Number(data?.lapseGraceVideoCount) || 0));
  const inLapseGrace = lapseGraceUntilMs > 0;
  // The server stops sending the date once it passes; the clamp only guards a
  // stale screen from quoting a day in the past.
  const lapseRemovalDate = inLapseGrace ? fmtRemovalDate(Math.max(lapseGraceUntilMs, Date.now())) : '';
  // Per-video era (owner-only field on each getMyVideos card). The server
  // sends a boolean; the callable would encode a missing value as null, so
  // the KEY being present is the signal. false = held without a paid
  // membership (normal free window and eviction); true (or null) = held as a
  // member. No key (an older server) = no per-video grace claim at all.
  const hasEra = (v) => Object.prototype.hasOwnProperty.call(v, 'heldUnderPaid');
  const isPaidEra = (v) => hasEra(v) && v.heldUnderPaid !== false;
  const isFreeEra = (v) => hasEra(v) && v.heldUnderPaid === false;
  const lapseBannerText = inLapseGrace && lapseGraceCount > 0
    ? (lapseGraceCount === 1
      ? `Your paid plan has ended. 1 older recording will be removed on ${lapseRemovalDate}. Download anything you want to keep before then. Renewing keeps only the recordings inside your plan's video window.`
      : `Your paid plan has ended. ${lapseGraceCount} older recordings will be removed on ${lapseRemovalDate}. Download anything you want to keep before then. Renewing keeps only the recordings inside your plan's video window.`)
    : null;

  // FIFO: the oldest video is next to be auto-deleted if that ceiling is hit.
  // Without a paid membership, eviction only ever takes videos held after the
  // membership ended (vods.js planEviction freeEraOnly), never a member-era
  // one, so only those can be "next". getMyVideos says so with
  // evictsFreeEraOnly, grace or not; inLapseGrace is the fallback for an older
  // server. protectsPaidEra gates the copy below: a never-paid free member has
  // only free-era videos, so today's "your oldest videos" copy and tag stay
  // true for them.
  // A paying owner can also carry a running grace date (labels only); the
  // server's evictsFreeEraOnly === false then wins, since eviction on a paid
  // account takes the oldest videos.
  const freeEraOnlyEviction = data?.evictsFreeEraOnly === true
    || (inLapseGrace && data?.evictsFreeEraOnly === undefined);
  const protectsPaidEra = freeEraOnlyEviction && videos.some(isPaidEra);
  const evictable = freeEraOnlyEviction ? videos.filter(isFreeEra) : videos;
  const oldestId = (videos.length > 1 && evictable.length > 0)
    ? evictable.reduce((a, b) => ((a.sessionStartMs || 0) < (b.sessionStartMs || 0) ? a : b)).id
    : null;

  // Countdown only where it is TRUE: retention deletion runs solely on videos
  // created after settings/videos.retentionEnforcedAfterMs, and 0 means the
  // sweep is off entirely. Videos from the GB era are grandfathered, so a
  // countdown on them would promise a deletion that never comes.
  const enforcedAfterMs = Number(storage.retentionEnforcedAfterMs || 0);
  const isUpload = (v) => v.source === 'upload';
  const expiryLabel = (v) => {
    // An upload is never time-expired, so it must never show a countdown —
    // that window belongs to stream videos only (founder 2026-08-03).
    if (isUpload(v)) return null;
    if (retentionDays <= 0 || enforcedAfterMs <= 0) return null;
    const createdMs = v.createdAt ? Date.parse(v.createdAt) : NaN;
    if (!Number.isFinite(createdMs) || createdMs <= enforcedAfterMs) return null;
    const windowEndMs = createdMs + retentionDays * 864e5;
    // Lapse grace: a member-era recording that leaves the free window before
    // the grace ends is kept until the grace date, so a countdown on it would
    // be wrong. One whose window outlasts the grace keeps its countdown. Same
    // strict test as the server's count, so the rows marked here are exactly
    // the lapseGraceVideoCount in the banner.
    if (inLapseGrace && isPaidEra(v) && windowEndMs < lapseGraceUntilMs) {
      return `removed on ${lapseRemovalDate}`;
    }
    const left = Math.max(0, Math.ceil((windowEndMs - Date.now()) / 864e5));
    return left === 0 ? 'expires today' : left === 1 ? 'expires tomorrow' : `expires in ${left} days`;
  };

  // Members-only videos (3.5a). Every field is read as `=== true` / a
  // positive count: an older server omits them, and then no row changes.
  // membersVideosAvailable = the owner may mark NEW members-only videos;
  // membersVideosPaused = the emergency hold; membersOnlyStranded = how many
  // members-only videos nobody else can watch now.
  const membersAvailable = data?.membersVideosAvailable === true;
  const membersPaused = data?.membersVideosPaused === true;
  const serving = !!data?.serving;
  const strandedCount = Math.max(0, Math.floor(Number(data?.membersOnlyStranded) || 0));
  const usesVisibility = (v) => membersAvailable || v.membersOnly === true || v.held === true;
  const visibilityOf = (v) => (v.membersOnly === true ? 'members' : (v.published ? 'public' : 'private'));
  // Choices that can't be made right now. The current one still renders
  // selected (a stranded or paused Members is selected but disabled).
  const visibilityBlocked = {
    private: false,
    members: !membersAvailable || membersPaused || !serving,
    public: !serving,
  };
  const visibilityHint = (v) => {
    const cur = visibilityOf(v);
    if (v.held === true) return 'Members-only videos are paused for a moment. Only you can watch this one until then.';
    if (cur === 'members' && !serving) return 'Only you can watch this now. You can make it Private.';
    if (membersPaused) return 'Members-only videos are paused for a moment.';
    if (cur === 'members' && !membersAvailable) {
      return strandedCount > 0
        ? 'Only you can watch this now. Choose Public or Private.'
        : 'Current members can still watch this.';
    }
    if (membersAvailable && serving) return 'Only members of your channel can watch it. Its title and thumbnail stay visible.';
    return null;
  };
  const strandedBanner = strandedCount > 0
    ? (strandedCount === 1
      ? `1 members-only video can only be watched by you right now. You can make it ${serving ? 'Public or Private' : 'Private'}.`
      : `${strandedCount} members-only videos can only be watched by you right now. You can make them ${serving ? 'Public or Private' : 'Private'}.`)
    : null;

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile'))}
        >
          <Ionicons name="chevron-back" size={18} color="#FCD000" />
          <Text style={styles.backLinkTxt}>Back</Text>
        </TouchableOpacity>
        <View style={styles.titleRow}>
          <Text style={styles.pageTitle}>My Videos</Text>
          {data?.serving && (
            <TouchableOpacity style={styles.uploadBtn} onPress={uploadVideo} disabled={!!uploading}>
              <Ionicons name="cloud-upload-outline" size={15} color="#000" />
              <Text style={styles.uploadBtnTxt}>Upload video</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Lapse grace warning: the same date the server's email names */}
        {lapseBannerText && (
          <View style={styles.lapseCard}>
            <View style={styles.lapseTop}>
              <Ionicons name="alert-circle-outline" size={18} color="#ffb84d" style={{ marginTop: 1 }} />
              <Text style={styles.lapseTxt}>{lapseBannerText}</Text>
            </View>
            <TouchableOpacity style={styles.subscribeBtn} onPress={() => navigation.navigate('Subscription')}>
              <Text style={styles.subscribeBtnTxt}>Renew</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Upload in progress */}
        {uploading && (
          <View style={styles.gaugeCard}>
            <Text style={styles.gaugeTitle} numberOfLines={1}>
              {uploading.finalizing ? 'Processing' : 'Uploading'} — {uploading.name}
            </Text>
            <View style={styles.bar}>
              <View style={[styles.barFill, { width: `${uploading.pct}%` }]} />
            </View>
            <Text style={styles.gaugeSub}>
              {uploading.finalizing
                ? 'Almost done — saving to your videos…'
                : `${uploading.pct}% · keep this page open until the upload finishes`}
            </Text>
          </View>
        )}

        {/* Retention card — days are the promise; the GB ceiling stays silent */}
        <View style={styles.gaugeCard}>
          {data?.serving ? (
            <>
              <View style={styles.gaugeTop}>
                {retentionDays > 0 ? (
                  <Text style={styles.gaugeTitle}>
                    {tierName} plan — stream videos kept for <Text style={{ color: '#FCD000' }}>{retentionDays} days</Text>
                    {storage.interval === 'year' ? ' (doubled on yearly)' : ''}
                  </Text>
                ) : (
                  // No window configured for this account (comped/admin) — say
                  // nothing rather than quote a "0 days" that isn't true.
                  <Text style={styles.gaugeTitle}>{tierName} plan — your stream videos are kept here</Text>
                )}
                <TouchableOpacity onPress={() => navigation.navigate('Subscription')}>
                  <Text style={styles.upgradeLink}>Upgrade</Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.gaugeSub}>
                New stream videos save here automatically
                {retentionDays > 0
                  ? ` · download anything you want to keep past ${retentionDays} days, or upgrade for a longer window`
                  : ''}
              </Text>
              {/* The other class. Uploads have no countdown at all — say so
                  plainly so nobody assumes the window above applies to them. */}
              <Text style={styles.gaugeSub}>
                {retentionDays > 0 ? 'That window is for stream videos, not your uploads. ' : ''}
                On a free account, or after a paid plan ends, uploads may be removed after a period we set;
                we&apos;ll email you first, with time to download them.
              </Text>
              {nearFull && (!protectsPaidEra ? (
                <Text style={styles.warnTxt}>
                  You&apos;re near the most your plan can hold — your oldest videos will be deleted to make room
                  for new ones. Download anything you want to keep.
                </Text>
              ) : evictable.length > 0 ? (
                // Eviction skips member-era videos for an owner without a paid
                // membership, so only the ones saved since it ended can go.
                <Text style={styles.warnTxt}>
                  You&apos;re near the most your plan can hold. To make room, your oldest videos saved without a
                  paid plan will be deleted first. Videos from your paid plan are not deleted to make room, so if
                  that isn&apos;t enough, new stream videos won&apos;t be saved. Download anything you want to keep, or renew.
                </Text>
              ) : (
                // Nothing here can be evicted, so once full a new stream video
                // is not saved at all (vods.js skips it) rather than making room.
                <Text style={styles.warnTxt}>
                  You&apos;re near the most your plan can hold. Videos from your paid plan are not deleted to make
                  room, so once you&apos;re full, new stream videos won&apos;t be saved. Download or delete older
                  videos to make room, or renew.
                </Text>
              ))}
            </>
          ) : (
            <>
              {/* Not served: a payment-risk hold, or a tier with no video window
                  (free before the Oct 2 config). The screen can't tell which, so
                  the copy is neutral and never claims a membership is required. */}
              <Text style={styles.gaugeTitle}>Uploads and publishing unavailable</Text>
              <Text style={styles.warnTxt}>
                Uploading and publishing aren&apos;t available on your account right now, and your videos aren&apos;t
                publicly viewable.
                {data?.graceDeadline
                  ? ` Your uploads may be removed on or after ${fmtDate(data.graceDeadline)}. Download anything you want to keep.`
                  : ''}
              </Text>
              <TouchableOpacity style={styles.subscribeBtn} onPress={() => navigation.navigate('Subscription')}>
                <Text style={styles.subscribeBtnTxt}>View plans</Text>
              </TouchableOpacity>
            </>
          )}
        </View>

        {/* Members-only videos only the owner can watch right now (3.5a) */}
        {strandedBanner && (
          <View style={styles.lapseCard}>
            <View style={styles.lapseTop}>
              <Ionicons name="lock-closed-outline" size={18} color="#ffb84d" style={{ marginTop: 1 }} />
              <Text style={styles.lapseTxt}>{strandedBanner}</Text>
            </View>
          </View>
        )}

        {/* Bulk actions — appear once one or more videos are selected */}
        {selected.size > 0 && (
          <View style={styles.bulkBar}>
            <Text style={styles.bulkCount}>{selected.size} selected</Text>
            <TouchableOpacity onPress={allSelected ? clearSelection : selectAll}>
              <Text style={styles.bulkLink}>{allSelected ? 'Clear all' : 'Select all'}</Text>
            </TouchableOpacity>
            <View style={{ flex: 1 }} />
            <TouchableOpacity style={styles.bulkBtn} onPress={downloadSelected} disabled={busy === 'bulk-dl'}>
              <Ionicons name="download-outline" size={15} color="#7FD4FF" />
              <Text style={[styles.bulkBtnTxt, { color: '#7FD4FF' }]}>{busy === 'bulk-dl' ? 'Downloading…' : 'Download'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.bulkBtn} onPress={deleteSelected} disabled={busy === 'bulk-del'}>
              <Ionicons name="trash-outline" size={15} color="#ff7a7a" />
              <Text style={[styles.bulkBtnTxt, { color: '#ff7a7a' }]}>{busy === 'bulk-del' ? 'Deleting…' : 'Delete'}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.bulkBtn} onPress={clearSelection}>
              <Text style={[styles.bulkBtnTxt, { color: '#999' }]}>Cancel</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Video rows */}
        {videos.length === 0 ? (
          <View style={styles.emptyBox}>
            <Ionicons name="videocam-outline" size={34} color="#555" />
            <Text style={styles.emptyTitle}>No videos yet</Text>
            <Text style={styles.emptyTxt}>
              Upload a video, or go live and your stream will land here automatically, ready to publish to your profile.
            </Text>
          </View>
        ) : videos.map((v) => (
          <View key={v.id} style={[styles.row, !v.published && { opacity: 0.85 }, selected.has(v.id) && styles.rowSelected, usesVisibility(v) && styles.rowWrap]}>
            <TouchableOpacity style={styles.checkbox} onPress={() => toggleSelect(v.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons
                name={selected.has(v.id) ? 'checkbox' : 'square-outline'}
                size={22}
                color={selected.has(v.id) ? '#FCD000' : '#555'}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.thumb}
              onPress={() => navigation.navigate('VideoWatch', { videoId: v.id })}
            >
              {v.thumbnailUrl ? (
                <Image source={{ uri: v.thumbnailUrl }} style={styles.thumbImg} resizeMode="cover" />
              ) : null}
              <Ionicons name="play" size={16} color="#ffffffcc" />
              <Text style={styles.thumbDur}>{fmtDuration(v.durationSeconds)}</Text>
              {v.held === true ? (
                <Text style={styles.thumbTag}>On hold</Text>
              ) : v.membersOnly === true ? (
                <Text style={styles.thumbTag}>MEMBERS</Text>
              ) : null}
            </TouchableOpacity>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.rowTitle} numberOfLines={1}>{v.title}</Text>
              <Text style={styles.rowMeta} numberOfLines={1}>
                {v.published ? `${v.views || 0} views · ${v.likes || 0} likes · ` : 'Not published · '}
                {fmtGB(v.sizeBytes)}
                {isUpload(v) ? ' · upload' : ''}
                {expiryLabel(v) ? <Text style={{ color: '#ffb84d' }}>{` · ${expiryLabel(v)}`}</Text> : null}
                {nearFull && v.id === oldestId ? ' · ' : ''}
                {nearFull && v.id === oldestId && <Text style={{ color: '#ffb84d' }}>oldest — next to be deleted</Text>}
              </Text>
              <View style={styles.rowActions}>
                <TouchableOpacity onPress={() => setEditing({
                  id: v.id, title: v.title, description: v.description || '', tags: (v.tags || []).join(', '), game: v.game || '', thumbnailUrl: v.thumbnailUrl || null,
                })}>
                  <Text style={styles.rowAction}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => download(v)} disabled={busy === `dl:${v.id}`}>
                  <Text style={[styles.rowAction, { color: '#7FD4FF' }]}>
                    {busy === `dl:${v.id}` ? 'Preparing…' : '⬇ Download'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => remove(v)} disabled={busy === `del:${v.id}`}>
                  <Text style={[styles.rowAction, { color: '#ff7a7a' }]}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
            {!usesVisibility(v) ? (
            <View style={{ alignItems: 'center' }}>
              <Switch
                value={!!v.published}
                onValueChange={() => togglePublish(v)}
                disabled={busy === `pub:${v.id}` || !data?.serving}
                trackColor={{ false: '#333', true: '#FCD000' }}
                thumbColor={v.published ? '#000' : '#888'}
              />
              <Text style={styles.switchLabel}>{v.published ? 'PUBLISHED' : 'PRIVATE'}</Text>
            </View>
            ) : (
              // One segmented control on its own full-width line under the row.
              <View style={styles.visWrap}>
                <View style={styles.segRow}>
                  {[['private', 'Private'], ['members', 'Members'], ['public', 'Public']].map(([key, label]) => {
                    const on = visibilityOf(v) === key;
                    const off = visibilityBlocked[key] || busy === `vis:${v.id}`;
                    return (
                      <TouchableOpacity
                        key={key}
                        style={[styles.seg, on && styles.segOn, visibilityBlocked[key] && styles.segBlocked]}
                        onPress={() => { if (!on && !off) setVisibility(v, key); }}
                        disabled={off}
                        accessibilityRole="button"
                        accessibilityLabel={label}
                        accessibilityState={{ selected: on, disabled: visibilityBlocked[key] }}
                      >
                        <Text style={[styles.segTxt, on && styles.segTxtOn]}>{label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                {visibilityHint(v) ? <Text style={styles.visHint}>{visibilityHint(v)}</Text> : null}
                {visibilityOf(v) === 'members' && v.held !== true ? (
                  <>
                    <Text style={styles.whoLabel}>{WHO_CAN_WATCH}</Text>
                    <View style={styles.segRow}>
                      {WHO_CAN_WATCH_OPTIONS.map((o) => {
                        const current = isTierKey(v.membersMinTier) ? v.membersMinTier : 'spark';
                        const on = current === o.value;
                        const off = busy === `tier:${v.id}` || busy === `vis:${v.id}`;
                        // Only a raise to a tier none of the creator's tiers reach is refused (C10).
                        const unreachable = whoCanWatchBlocked(data?.membersVideosTiers, current, o.value);
                        return (
                          <TouchableOpacity
                            key={o.value}
                            style={[styles.seg, on && styles.segOn, unreachable && styles.segBlocked]}
                            onPress={() => {
                              if (on || off) return;
                              if (unreachable) { showError('Could not update video', VIDEO_TIER_UNREACHABLE); return; }
                              setMinTier(v, o.value);
                            }}
                            disabled={off}
                            accessibilityRole="button"
                            accessibilityLabel={`${WHO_CAN_WATCH}: ${o.label}`}
                            accessibilityState={{ selected: on, disabled: off || unreachable }}
                          >
                            <Text style={[styles.segTxt, on && styles.segTxtOn]}>{o.label}</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </>
                ) : null}
              </View>
            )}
          </View>
        ))}

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Edit modal */}
      <Modal visible={!!editing} transparent animationType="fade" onRequestClose={() => setEditing(null)}>
        <View style={styles.modalOverlay}>
          <Pressable style={styles.modalScrim} onPress={() => setEditing(null)} />
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Edit video</Text>
            <Text style={styles.fieldLabel}>Title</Text>
            <TextInput
              style={styles.input}
              value={editing?.title || ''}
              onChangeText={(t) => setEditing((e) => ({ ...e, title: t }))}
              maxLength={80}
            />
            <Text style={styles.fieldLabel}>Description</Text>
            <TextInput
              style={[styles.input, { minHeight: 70 }]}
              value={editing?.description || ''}
              onChangeText={(t) => setEditing((e) => ({ ...e, description: t }))}
              maxLength={500}
              multiline
            />
            <Text style={styles.fieldLabel}>Game</Text>
            <TextInput
              style={styles.input}
              value={editing?.game || ''}
              onChangeText={(t) => setEditing((e) => ({ ...e, game: t }))}
              placeholder="e.g. Madden"
              placeholderTextColor="#666"
              maxLength={40}
            />
            <Text style={styles.fieldLabel}>Tags (comma-separated)</Text>
            <TextInput
              style={styles.input}
              value={editing?.tags || ''}
              onChangeText={(t) => setEditing((e) => ({ ...e, tags: t }))}
              placeholder="comeback, 4thquarter"
              placeholderTextColor="#666"
            />
            <Text style={styles.fieldLabel}>Thumbnail</Text>
            <TouchableOpacity style={styles.thumbPicker} onPress={pickVideoThumbnail} disabled={uploadingThumb}>
              {editing?.thumbnailUrl ? (
                <Image source={{ uri: editing.thumbnailUrl }} style={styles.thumbPickerImg} resizeMode="cover" />
              ) : (
                <Ionicons name="image-outline" size={26} color="#666" />
              )}
              <Text style={styles.thumbPickerHint}>
                {uploadingThumb
                  ? 'Uploading…'
                  : `Tap to ${editing?.thumbnailUrl ? 'change' : 'upload'} — 1280×720, 2MB max. No pick uses your stream thumbnail.`}
              </Text>
            </TouchableOpacity>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditing(null)}>
                <Text style={styles.cancelBtnTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={saveEdit} disabled={busy === 'edit'}>
                {busy === 'edit'
                  ? <ActivityIndicator size="small" color="#000" />
                  : <Text style={styles.saveBtnTxt}>Save</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0b0b0b' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scroll: { padding: 16, maxWidth: 760, width: '100%', alignSelf: 'center' },
  backLink: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  backLinkTxt: { color: '#FCD000', fontWeight: '700', fontSize: 14 },
  pageTitle: { color: '#fff', fontSize: 22, fontWeight: '800' },
  titleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  uploadBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 13,
  },
  uploadBtnTxt: { color: '#000', fontWeight: '800', fontSize: 12.5 },

  gaugeCard: { backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424', borderRadius: 12, padding: 14, marginBottom: 16 },
  gaugeTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  gaugeTitle: { color: '#fff', fontSize: 14, fontWeight: '800' },
  upgradeLink: { color: '#FCD000', fontSize: 12.5, fontWeight: '800' },
  bar: { height: 7, backgroundColor: '#222', borderRadius: 4, overflow: 'hidden', marginTop: 10 },
  barFill: { height: '100%', backgroundColor: '#FCD000' },
  gaugeSub: { color: '#aaa', fontSize: 12, marginTop: 7 },
  warnTxt: { color: '#ffb84d', fontSize: 12.5, fontWeight: '600', marginTop: 7, lineHeight: 18 },
  subscribeBtn: { backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 10, alignItems: 'center', marginTop: 12 },
  subscribeBtnTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  lapseCard: { backgroundColor: '#1a1408', borderWidth: 1, borderColor: '#5a4212', borderRadius: 12, padding: 14, marginBottom: 16 },
  lapseTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  lapseTxt: { flex: 1, color: '#ffcf8a', fontSize: 13, fontWeight: '600', lineHeight: 19 },

  emptyBox: { alignItems: 'center', padding: 36, backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424', borderRadius: 12 },
  emptyTitle: { color: '#fff', fontSize: 15, fontWeight: '800', marginTop: 10 },
  emptyTxt: { color: '#aaa', fontSize: 13, textAlign: 'center', marginTop: 6, lineHeight: 19, maxWidth: 320 },

  row: {
    flexDirection: 'row', gap: 12, alignItems: 'center',
    backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424',
    borderRadius: 12, padding: 12, marginBottom: 10,
  },
  rowSelected: { borderColor: '#FCD000', backgroundColor: '#15130a' },
  checkbox: { paddingRight: 2, alignItems: 'center', justifyContent: 'center' },
  bulkBar: {
    flexDirection: 'row', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    backgroundColor: '#15130a', borderWidth: 1, borderColor: '#5a4d12',
    borderRadius: 12, paddingVertical: 10, paddingHorizontal: 14, marginBottom: 12,
  },
  bulkCount: { color: '#FCD000', fontSize: 13, fontWeight: '800' },
  bulkLink: { color: '#cfcfd6', fontSize: 12.5, fontWeight: '700' },
  bulkBtn: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  bulkBtnTxt: { fontSize: 12.5, fontWeight: '800' },
  thumb: {
    width: 96, aspectRatio: 16 / 9, borderRadius: 8, backgroundColor: '#181818',
    alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden',
  },
  thumbImg: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  thumbPicker: {
    borderWidth: 1, borderColor: '#333', borderRadius: 10, backgroundColor: '#131313',
    minHeight: 120, alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    marginTop: 2,
  },
  thumbPickerImg: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  thumbPickerHint: {
    color: '#ccc', fontSize: 11.5, fontWeight: '700', textAlign: 'center',
    backgroundColor: '#000000aa', paddingVertical: 4, paddingHorizontal: 8, borderRadius: 6,
    position: 'absolute', bottom: 6,
  },
  thumbDur: {
    position: 'absolute', right: 4, bottom: 4, backgroundColor: '#000000cc',
    color: '#fff', fontSize: 9.5, fontWeight: '700', borderRadius: 4, paddingHorizontal: 4, overflow: 'hidden',
  },
  rowTitle: { color: '#fff', fontSize: 13.5, fontWeight: '700' },
  rowMeta: { color: '#aaa', fontSize: 11.5, marginTop: 3 },
  rowActions: { flexDirection: 'row', gap: 14, marginTop: 6, flexWrap: 'wrap' },
  rowAction: { color: '#aaa', fontSize: 12, fontWeight: '700' },
  switchLabel: { color: '#aaa', fontSize: 8.5, fontWeight: '800', letterSpacing: 0.5, marginTop: 4 },
  // Members-only visibility (3.5a)
  rowWrap: { flexWrap: 'wrap' },
  thumbTag: {
    position: 'absolute', left: 4, top: 4, backgroundColor: '#000000cc',
    color: '#FCD000', fontSize: 8.5, fontWeight: '800', letterSpacing: 0.4, borderRadius: 4, paddingHorizontal: 4, overflow: 'hidden',
  },
  visWrap: { flexBasis: '100%', width: '100%' },
  segRow: { flexDirection: 'row', gap: 6 },
  seg: {
    flex: 1, minHeight: 36, borderRadius: 8, borderWidth: 1, borderColor: '#333',
    backgroundColor: '#1a1a1a', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4,
  },
  segOn: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  segBlocked: { opacity: 0.45 },
  segTxt: { color: '#ddd', fontSize: 12.5, fontWeight: '800' },
  segTxtOn: { color: '#000' },
  visHint: { color: '#aaa', fontSize: 11.5, marginTop: 6, lineHeight: 16 },
  whoLabel: { color: '#ddd', fontSize: 11.5, fontWeight: '800', marginTop: 10, marginBottom: 6 },

  modalOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  modalScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)' },
  modalCard: {
    width: '100%', maxWidth: 460, backgroundColor: '#101010',
    borderWidth: 1, borderColor: '#333', borderRadius: 14, padding: 18,
  },
  modalTitle: { color: '#fff', fontSize: 17, fontWeight: '800', marginBottom: 12 },
  fieldLabel: { color: '#aaa', fontSize: 11.5, fontWeight: '700', marginTop: 10, marginBottom: 5, letterSpacing: 0.3 },
  input: {
    backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333', borderRadius: 8,
    color: '#fff', fontSize: 13.5, paddingHorizontal: 12, paddingVertical: 9,
  },
  modalActions: { flexDirection: 'row', gap: 10, marginTop: 18 },
  cancelBtn: { flex: 1, backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#333', borderRadius: 8, paddingVertical: 11, alignItems: 'center' },
  cancelBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 13.5 },
  saveBtn: { flex: 1, backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 11, alignItems: 'center' },
  saveBtnTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
});
